import { create } from 'zustand';
import { api, onPasswordChangeRequired, setAccessToken, setActiveBranchUid } from '../../lib/api-client';
import { clearOfflineCache } from '../../lib/offline-cache';

export interface SessionBranch {
  uid: string;
  code: string;
  name: string;
}

export interface SessionUser {
  uid: string;
  name: string;
  username: string;
  /** Contact, not identity, and optional. */
  email: string | null;
  roles: string[];
  /**
   * What the roles add up to. For leaving out an action the API would refuse;
   * the API still checks every call, so this is never the authority.
   * Optional because a web deployed before the API that sends it gets a
   * user without it, and that must hide a tab rather than crash the page.
   */
  permissions?: string[];
  /**
   * Somebody else chose their password (an import, Add user, a reset). The API
   * refuses everything else until they choose their own, so the app shows only
   * that screen. Optional for the same reason as `permissions`.
   */
  mustChangePassword?: boolean;
}

interface LoginResponse {
  accessToken: string;
  user: SessionUser;
  branches: SessionBranch[];
}

type MeResponse = Omit<LoginResponse, 'accessToken'>;

interface SessionState {
  user: SessionUser | null;
  branches: SessionBranch[];
  activeBranchUid: string | null;
  /** Undefined while the initial /auth/me probe is still running. */
  status: 'unknown' | 'signed-in' | 'signed-out';
  signIn: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
  setBranch: (uid: string) => void;
}

/**
 * Session state, and the ONLY place the access token and active branch are set.
 *
 * The token lives in module memory inside api-client, not in localStorage or in
 * this store's persisted state: anything readable by JavaScript on the page is
 * readable by an XSS. Losing the token on refresh is fine — the httpOnly cookie
 * restores it through /auth/refresh.
 */
export const useSession = create<SessionState>((set, get) => ({
  user: null,
  branches: [],
  activeBranchUid: null,
  status: 'unknown',

  signIn: async (username, password, rememberMe) => {
    const res = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { username, password, rememberMe: rememberMe === true },
      withoutBranch: true,
    });
    setAccessToken(res.accessToken);
    const branch = res.branches[0]?.uid ?? null;
    setActiveBranchUid(branch);
    set({ user: res.user, branches: res.branches, activeBranchUid: branch, status: 'signed-in' });
  },

  signOut: async () => {
    try {
      await api<undefined>('/auth/logout', { method: 'POST', withoutBranch: true });
    } finally {
      setAccessToken(null);
      setActiveBranchUid(null);
      // A shared showroom tablet must not hand the next advisor the previous
      // one's customer list. Runs in `finally`: a logout call that fails still
      // has to clear what is on this device.
      clearOfflineCache();
      set({ user: null, branches: [], activeBranchUid: null, status: 'signed-out' });
    }
  },

  /**
   * Called once at startup. A page reload has no access token but may still hold
   * the refresh cookie, so this asks the API who we are; the api() helper
   * transparently refreshes on the 401.
   */
  restore: async () => {
    try {
      const res = await api<MeResponse>('/auth/me', { withoutBranch: true });
      const branch = get().activeBranchUid ?? res.branches[0]?.uid ?? null;
      setActiveBranchUid(branch);
      set({ user: res.user, branches: res.branches, activeBranchUid: branch, status: 'signed-in' });
    } catch {
      set({ user: null, branches: [], activeBranchUid: null, status: 'signed-out' });
    }
  },

  setBranch: (uid) => {
    setActiveBranchUid(uid);
    set({ activeBranchUid: uid });
  },
}));

// The router then shows only "Choose your own password" instead of every
// screen failing with the same refusal.
onPasswordChangeRequired(() => {
  const user = useSession.getState().user;
  if (user !== null && user.mustChangePassword !== true) {
    useSession.setState({ user: { ...user, mustChangePassword: true } });
  }
});

/** True when the signed-in user holds `code`. Display only, never a guard. */
export function useHasPermission(code: string): boolean {
  return useSession((s) => s.user?.permissions?.includes(code) ?? false);
}
